import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { subscribeNotificationInsert, subscribeNotificationReconnect } from "@/lib/notificationsBus";
import { useRealtimeSelfHeal } from "@/hooks/useRealtimeSelfHeal";

/**
 * Data-refresh subscriber for carrier-side offer updates.
 * Toast + chime are owned by useGlobalNotificationAlerts — this hook only
 * invalidates React Query caches so open UIs update in realtime.
 */
export function useCarrierOfferNotifications() {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();

  const isCarrierLike =
    profile?.user_type === "carrier" || profile?.user_type === "broker_carrier";
  const userId = user?.id;

  useEffect(() => {
    if (!userId || !isCarrierLike) return;

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ["carrier-offers", userId] });
      queryClient.invalidateQueries({ queryKey: ["carrier-offers-full", userId] });
    };

    const relevantTypes = new Set(["counter_offer", "offer_accepted", "offer_rejected"]);
    const offNotif = subscribeNotificationInsert(userId, (row) => {
      if (!relevantTypes.has((row as { type?: string })?.type ?? "")) return;
      invalidate();
    });

    const offersChannel = supabase
      .channel(`carrier-offers-updates-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "offers",
          filter: `created_by=eq.${userId}`,
        },
        () => { invalidate(); }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") invalidate();
      });

    const offReconnect = subscribeNotificationReconnect(userId, () => invalidate());

    return () => {
      offNotif();
      offReconnect();
      supabase.removeChannel(offersChannel);
    };
  }, [userId, isCarrierLike, queryClient]);

  useRealtimeSelfHeal(
    () => {
      if (!userId) return;
      queryClient.invalidateQueries({ queryKey: ["carrier-offers", userId] });
      queryClient.invalidateQueries({ queryKey: ["carrier-offers-full", userId] });
    },
    !!userId && isCarrierLike,
  );
}
